import { useEffect, useState, useRef, useCallback, type CSSProperties, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { Footer } from '../components/shell/Footer';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { apiClient, ApiRequestError } from '../network/ApiClient';
import { SessionChannel } from '../network/SessionChannel';
import { AppSoundManager } from '../audio/AppSoundManager';
import { InviteToGameModal } from '../components/social/InviteToGameModal';
import { useConfirm } from '../context/ConfirmContext';
import { CountryFlag } from '../components/ui/CountryFlag';
import { PlayerAvatar } from '../components/ui/PlayerAvatar';
import type { LobbyState, SessionDetail, SessionPlayer } from '../types/game';
import { TABLE_SKIN_CATALOG, TILE_SKIN_CATALOG, type AppearanceMode } from '../config/viewerCosmetics';
import {
  deriveLobbyControls, findMe, computePlayEnabled, effectiveCosmeticsForViewer, showsReadyGlow,
} from './lobby/lobbyLogic';
import { CosmeticCatalog, SegmentedControl, type CatalogItem } from './lobby/catalogComponents';
import { tableSkinDisplayName, tileSkinDisplayName } from '../i18n/cosmetics';
import { getPlayerName } from '../utils/playerName';
import { LobbyTablePreview } from './lobby/LobbyTablePreview';
import { useTranslation } from '../i18n/react';
import { renderUiMessage, uiFromError, uiKey, type UiMessage } from '../i18n/uiMessage';

type Difficulty = 'easy' | 'medium';

// Figma-benchmarked constrained content composition (NOT the full AppShell
// width): the lobby block is ~1120px wide, centered beside the sidebar.
const CONTENT_MAX = 1120;

// Figma action buttons (Play / Get Ready / Leave Table) — stacked, EQUAL size.
// Scaled from the Figma 368×86.62 frame (~0.74× of the 2560 canvas).
const ACTION_W = 220;
const ACTION_H = 52;

const TEAM_COLOR: Record<number, string> = { 0: DT.color.teamRed, 1: DT.color.teamBlue };
// Display labels resolve at RENDER time through the shared cosmetic resolver
// (never at module import), so a runtime language switch relabels the open
// lobby's catalogs. Ids and assets are the stable values and never change.
const tableItems = (): CatalogItem[] =>
  TABLE_SKIN_CATALOG.map((t) => ({ id: t.id, label: tableSkinDisplayName(t.id, t.label), image: t.asset }));
const tileItems = (): CatalogItem[] =>
  TILE_SKIN_CATALOG.map((t) => ({ id: t.id, label: tileSkinDisplayName(t.id, t.label), image: t.thumbnail }));

function eyebrowStyle(isMobile: boolean): CSSProperties {
  return {
    fontFamily:    DS2.font.body,
    fontWeight:    400,
    fontSize:      isMobile ? 12 : `clamp(1rem, ${DS2.type.eyebrow.pref}vw, ${DS2.type.eyebrow.max}px)`,
    letterSpacing: '0.158em',
    textTransform: 'uppercase',
    color:         DS2.color.gold,
  };
}
function titleStyle(isMobile: boolean): CSSProperties {
  return {
    fontFamily:    DS2.font.heading,
    fontWeight:    700,
    fontSize:      isMobile ? 28 : `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
    letterSpacing: '-0.02em',
    lineHeight:    isMobile ? 1.1 : DS2.type.sectionHeading.lineHeight,
    color:         '#F5F0E0',
    margin:        '10px 0 0',
  };
}
function subtitleStyle(isMobile: boolean): CSSProperties {
  return {
    fontFamily: DS2.font.body,
    fontWeight: 300,
    fontSize:   isMobile ? 14 : `clamp(0.93rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
    lineHeight: isMobile ? 1.6 : DS2.type.paragraph.lineHeight,
    color:      DS2.color.textBright,
    margin:     '12px 0 0',
    maxWidth:   760,
  };
}

function SectionLabel({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ fontFamily: DS2.font.heading, fontWeight: 700, fontSize: isMobile ? 13 : 'clamp(0.8rem, 0.85vw, 15px)', letterSpacing: '0.12em', textTransform: 'uppercase', color: DS2.color.gold }}>
      {children}
    </div>
  );
}

// One seat row inside the team panel (Figma roster row).
function SeatRow({ position, player, isMe, canManage, canRemove, onAddBot, onInvite, onRemove, busy }: {
  position: number; player?: SessionPlayer; isMe: boolean; canManage: boolean; canRemove: boolean;
  onAddBot: (p: number) => void; onInvite: () => void; onRemove: (p: SessionPlayer) => void; busy: boolean;
}) {
  const isMobile = useIsMobile();
  const { t } = useTranslation('play');
  const team = position % 2;
  const color = TEAM_COLOR[team];
  const seatNo = team === 0 ? (position === 0 ? 1 : 2) : (position === 1 ? 1 : 2);
  const ready = showsReadyGlow(player);
  const avatarSize = isMobile ? 30 : 30;
  const nameFontSize = isMobile ? 14.5 : 15;
  // Shared structured label seam (Phase 9): bots with a stable bot_difficulty
  // get the localized label; humans (and unknown difficulties) stay verbatim.
  const seatName = player ? getPlayerName(player, player.display_name) : '';
  return (
    <div className="lobby-seat-row" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, padding: isMobile ? '9px 0' : '9px 0', minHeight: isMobile ? 40 : 40, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
      <span style={{ color, fontFamily: DS2.font.body, fontWeight: 500, fontSize: nameFontSize, width: isMobile ? 14 : 16, flexShrink: 0 }}>{seatNo}</span>
      {player ? (
        <>
          {/* Flag slot before avatar/name (humans only; bots use no flag).
              Unset/invalid countries show the neutral globe placeholder. */}
          {!player.is_bot && (
            <CountryFlag countryCode={player.country_code} width={isMobile ? 20 : 20} height={isMobile ? 14 : 14} loading="eager" />
          )}
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: avatarSize, height: avatarSize, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
            background: color, color: DT.color.white,
            fontFamily: DS2.font.heading, fontWeight: 700, fontSize: 12,
            border: '1px solid #FFFFFF',
            boxShadow: ready ? `0 0 0 2px ${DT.color.success}, 0 0 12px 2px ${DT.color.success}` : 'none',
          }} data-ready={ready ? 'true' : 'false'} aria-label={ready ? t('lobby.seatAriaReady', { name: seatName }) : t('lobby.seatAria', { name: seatName })}>
            {/* Authoritative avatar (Player Identity V1): human preset or the
                bot's stable per-session assignment; no key → name initial.
                Never a client-picked/seeded avatar. */}
            <PlayerAvatar
              avatarKey={player.avatar_key}
              name={seatName}
              size={avatarSize}
              loading="eager"
              style={{ width: '100%', height: '100%' }}
              initialStyle={{
                background: 'transparent', border: 'none',
                fontFamily: DS2.font.heading, fontWeight: 700, fontSize: 12,
              }}
            />
          </span>
          <span title={seatName} style={{ color: DT.color.textPrimary, fontFamily: DS2.font.body, fontWeight: 500, fontSize: nameFontSize, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? 120 : undefined }}>
            {seatName}
          </span>
          {player.is_host && <Badge text={t('lobby.badgeHost')} color={DS2.color.gold} />}
          {isMe && !player.is_host && <Badge text={t('lobby.badgeYou')} color={color} />}
          {player.is_bot && <Badge text={t('lobby.badgeBot')} color={DT.color.textMuted} />}
          {ready && <Badge text={t('lobby.badgeReady')} color={DT.color.success} />}
          {/* Host-only remove control — compact ×, revealed on hover/focus. */}
          {canRemove && (
            <button
              className="lobby-remove-seat"
              onClick={() => onRemove(player)}
              disabled={busy}
              aria-label={t('lobby.removeSeat', { name: seatName })}
              title={t('lobby.removeSeat', { name: seatName })}
              style={{
                // Always visible (not hover-only) and clearly actionable: a gold
                // ring with a filled tint; intensifies to red on hover/focus.
                marginLeft: 'auto', width: isMobile ? 24 : 24, height: isMobile ? 24 : 24, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%', border: `1.5px solid ${DS2.color.goldOutlineText}`,
                background: 'rgba(212,175,55,0.14)', color: DS2.color.goldOutlineText,
                cursor: busy ? 'default' : 'pointer', lineHeight: 1, fontSize: isMobile ? 14 : 14, fontWeight: 700,
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </>
      ) : (
        <>
          <span style={{ color, fontFamily: DS2.font.body, fontStyle: 'italic', fontWeight: 500, fontSize: nameFontSize, flex: 1 }}>{t('lobby.emptySeat')}</span>
          {canManage && (
            <span style={{ display: 'flex', gap: isMobile ? 6 : 10, flex: isMobile ? '1 1 100%' : undefined, marginLeft: isMobile ? 22 : 0 }}>
              <PillButton label={t('lobby.inviteFriend')} onClick={onInvite} disabled={busy} variant="gold" />
              <PillButton label={t('lobby.addBot')} onClick={() => onAddBot(position)} disabled={busy} variant="light" />
            </span>
          )}
        </>
      )}
    </div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span style={{ fontFamily: DS2.font.body, fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#0B0B0B', background: color, padding: '3px 8px', borderRadius: DT.radius.pill }}>
      {text}
    </span>
  );
}

function PillButton({ label, onClick, disabled, variant }: { label: string; onClick: () => void; disabled?: boolean; variant: 'gold' | 'light' }) {
  const isMobile = useIsMobile();
  const gold = variant === 'gold';
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: DS2.font.body, fontWeight: 500, fontSize: isMobile ? 11 : 13,
      padding: isMobile ? '5px 11px' : '6px 16px', borderRadius: DT.radius.pill, cursor: disabled ? 'default' : 'pointer',
      background: gold ? DS2.color.gold : 'rgba(217,217,217,0.92)',
      color: '#0B0B0B', border: 'none', whiteSpace: 'nowrap',
      opacity: disabled ? 0.6 : 1,
    }}>{label}</button>
  );
}

// Figma Public/Private descriptive radio card (~355×162, host only).
function VisibilityCard({ value, selected, title, description, onSelect }: {
  value: 'public' | 'private'; selected: boolean; title: string; description: string; onSelect: (v: string) => void;
}) {
  const isMobile = useIsMobile();
  return (
    <button
      type="button" role="radio" aria-checked={selected} aria-label={title}
      onClick={() => onSelect(value)}
      style={{
        flex: isMobile ? '1 1 100%' : '1 1 240px', minWidth: isMobile ? 0 : 220, maxWidth: isMobile ? 'none' : 300, minHeight: isMobile ? 0 : 120, boxSizing: 'border-box',
        textAlign: 'left', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 10,
        padding: isMobile ? '14px 16px' : '18px 20px', borderRadius: 8,
        background: selected ? 'rgba(201,168,76,0.19)' : '#0B0B0B',
        border: `1px solid ${DS2.color.gold}`,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? 10 : 10 }}>
        <span aria-hidden="true" style={{
          width: isMobile ? 16 : 16, height: isMobile ? 16 : 16, borderRadius: '50%', flexShrink: 0,
          border: `2px solid ${selected ? DS2.color.gold : DS2.color.textBright}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {selected && <span style={{ width: isMobile ? 9 : 9, height: isMobile ? 9 : 9, borderRadius: '50%', background: DS2.color.gold }} />}
        </span>
        <span style={{ fontFamily: DS2.font.body, fontWeight: 700, fontSize: isMobile ? 14 : 'clamp(0.85rem, 0.9vw, 15px)', letterSpacing: '0.14em', textTransform: 'uppercase', color: selected ? DS2.color.gold : DS2.color.textBright }}>
          {title}
        </span>
      </span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: isMobile ? 12.5 : 'clamp(0.78rem, 0.85vw, 13.5px)', lineHeight: 1.5, color: DS2.color.textBright }}>
        {description}
      </span>
    </button>
  );
}

export function LobbyScreen() {
  const isMobile = useIsMobile();
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation('play');
  const { t: tc } = useTranslation('common');

  const [lobby, setLobby] = useState<LobbyState | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  // Keys for frontend-owned copy, raw for backend prose (i18n/uiMessage.ts).
  const [error, setError] = useState<UiMessage | null>(null);
  const [notice, setNotice] = useState<UiMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [inviteOpen, setInviteOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigatedRef = useRef(false);
  const prevThemeRef = useRef<string>('');
  const prevHostRef = useRef<number | null>(null);
  // Read inside applyLobby so it does NOT depend on the async-loaded user id —
  // otherwise the realtime effect below re-subscribes when getMe resolves and
  // can orphan a WebSocket subscription (subscribe() is async; cleanup would
  // no-op before it resolves). The effect now keys only on sessionId.
  const currentUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    apiClient.getMe().then((me) => { currentUserIdRef.current = me.id; setCurrentUserId(me.id); }).catch(() => {});
  }, []);

  const navigateToGame = useCallback((gameId: number) => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    if (pollRef.current) clearInterval(pollRef.current);
    navigate(`/game/${gameId}`, { replace: true, state: { sessionId } });
  }, [navigate, sessionId]);

  const applyLobby = useCallback((next: LobbyState) => {
    const myId = currentUserIdRef.current;
    const theme = `${next.host_table_skin}|${next.host_tile_skin}`;
    if (prevHostRef.current != null && next.host_user_id != null && prevHostRef.current !== next.host_user_id) {
      // Host reassignment (the previous host left the waiting lobby).
      const newHost = next.players.find((p) => p.is_host);
      if (newHost) {
        setNotice(newHost.user_id === myId
          ? uiKey('lobby.noticeYouAreHost')
          : uiKey('lobby.noticeNewHost', { name: newHost.display_name }));
      }
    } else if (prevThemeRef.current && prevThemeRef.current !== theme) {
      // Host changed the featured theme (guests must re-confirm Ready).
      const meNow = next.players.find((p) => p.user_id === myId);
      if (meNow && !meNow.is_host) setNotice(uiKey('lobby.noticeThemeChanged'));
    }
    prevThemeRef.current = theme;
    prevHostRef.current = next.host_user_id;
    setLobby(next);
    if (next.status === 'active' && next.current_game != null) navigateToGame(next.current_game);
  }, [navigateToGame]);

  const fetchLobby = useCallback(async () => {
    try {
      const data: SessionDetail = await apiClient.getSession(sessionId);
      applyLobby(data);
    } catch { setError(uiKey('lobby.errorLoad')); }
  }, [sessionId, applyLobby]);

  useEffect(() => {
    fetchLobby();
    pollRef.current = setInterval(fetchLobby, 5000); // fallback; broadcast is primary
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchLobby]);

  useEffect(() => {
    const channel = new SessionChannel(sessionId);
    channel.onLobbyState((m) => applyLobby(m.lobby));
    channel.onLobbyUpdated((m) => applyLobby(m.lobby));
    channel.onNextGameCreated((m) => navigateToGame(m.game_id)); // sync ALL players
    channel.onSessionAbandoned(() => { if (pollRef.current) clearInterval(pollRef.current); navigate('/play/online'); });
    // Host removed a participant — only the removed client leaves the lobby.
    channel.onParticipantRemoved((m) => {
      if (m.removed_user_id !== currentUserIdRef.current) return;
      if (pollRef.current) clearInterval(pollRef.current);
      navigatedRef.current = true;
      AppSoundManager.play('deny');
      navigate('/play/online', { state: { notice: m.message } });
    });
    channel.subscribe().catch((err) => console.error('[LobbyScreen] SessionChannel error:', err));
    return () => channel.unsubscribe();
  }, [sessionId, applyLobby, navigateToGame, navigate]);

  const me = findMe(lobby, currentUserId);
  const isHost = me?.is_host ?? false;
  const controls = deriveLobbyControls(isHost);
  const effective = effectiveCosmeticsForViewer(lobby, me);
  const playEnabled = computePlayEnabled(lobby);
  const appearanceMode: AppearanceMode = me?.appearance_mode ?? 'host';
  const amReady = me?.ready ?? false;
  const catalogsEditable = isHost || appearanceMode === 'personal';

  async function run(fn: () => Promise<LobbyState>) {
    setError(null); setBusy(true);
    try { applyLobby(await fn()); }
    catch (e) { setError(uiFromError(e, 'lobby.errorGeneric')); }
    finally { setBusy(false); }
  }

  const onSelectTable = (id: string) => {
    AppSoundManager.play('click');
    if (isHost) void run(() => apiClient.setHostTheme(sessionId, { host_table_skin: id }));
    else if (appearanceMode === 'personal') void run(() => apiClient.setAppearance(sessionId, { selected_table_skin: id }));
  };
  const onSelectTile = (id: string) => {
    AppSoundManager.play('click');
    if (isHost) void run(() => apiClient.setHostTheme(sessionId, { host_tile_skin: id }));
    else if (appearanceMode === 'personal') void run(() => apiClient.setAppearance(sessionId, { selected_tile_skin: id }));
  };
  const onAppearanceMode = (mode: string) => {
    AppSoundManager.play('click');
    setNotice(null);
    void run(() => apiClient.setAppearance(sessionId, { appearance_mode: mode as AppearanceMode }));
  };
  const onVisibility = (v: string) => { AppSoundManager.play('click'); void run(() => apiClient.setVisibility(sessionId, v as 'public' | 'private')); };
  const onAddBot = (position: number) => {
    AppSoundManager.play('click');
    void run(async () => { await apiClient.addBot(sessionId, position, difficulty); return apiClient.getSession(sessionId); });
  };
  const onRemove = async (p: SessionPlayer) => {
    AppSoundManager.play('click');
    // Two whole sentences (bot vs named player) — never a noun spliced in.
    const ok = await confirm({
      title: t('lobby.removeTitle'),
      message: p.is_bot ? t('lobby.removeBotMessage') : t('lobby.removeMessage', { name: p.display_name }),
      confirmLabel: t('lobby.removeConfirm'), cancelLabel: tc('action.cancel'), variant: 'danger',
    });
    if (!ok) return;
    void run(() => apiClient.removeSeat(sessionId, p.user_id));
  };
  const onReady = () => {
    AppSoundManager.play('click'); setNotice(null);
    void run(() => (amReady ? apiClient.clearReady(sessionId) : apiClient.setReady(sessionId)));
  };
  async function onPlay() {
    AppSoundManager.play('click'); setError(null); setBusy(true);
    try { const res = await apiClient.startGame(sessionId); navigateToGame(res.game_id); }
    catch (e) { setError(uiFromError(e, 'lobby.errorStart')); setBusy(false); }
  }
  async function onLeave() {
    AppSoundManager.play('click'); setBusy(true);
    try { await apiClient.leaveSession(sessionId); navigate('/play/online'); }
    catch (e) {
      // Already-gone outcomes count as a successful leave: the session was
      // destroyed by an earlier (possibly lost-response) leave, or this seat
      // was already released. Retrying can never succeed — navigating away is
      // the correct result, not an error loop.
      // Phase 12 (Slice C1): decided by the backend's STABLE codes — never by
      // comparing English prose. An uncoded/unknown error follows the normal
      // error path below and can never accidentally trigger this navigation.
      if (e instanceof ApiRequestError && (e.code === 'session_not_found' || e.code === 'not_in_session')) {
        navigate('/play/online');
        return;
      }
      setError(uiKey('lobby.errorLeave')); setBusy(false);
    }
  }

  const scopedCss = `
    .lobby-remove-seat { transition: color .15s ease, border-color .15s ease, background .15s ease, transform .1s ease; }
    .lobby-remove-seat:hover, .lobby-remove-seat:focus-visible { color: #FFFFFF; border-color: ${DT.color.error}; background: ${DT.color.error}; transform: scale(1.08); }
    .lobby-remove-seat:focus-visible { outline: 2px solid ${DT.color.error}; outline-offset: 2px; }
    .lobby-leave-btn:hover, .lobby-leave-btn:focus-visible { border-color: ${DT.color.error}; color: ${DT.color.error}; }
    .lobby-leave-btn:focus-visible { outline: 2px solid ${DT.color.error}; outline-offset: 2px; }
  `;

  // Figma: solid #131313 surface, gold border, bold gold uppercase text, no icon,
  // SAME width/height as Play. Red only on hover/focus (scoped CSS).
  const leaveButton = (
    <button
      className="lobby-leave-btn"
      onClick={onLeave}
      disabled={busy}
      aria-label={t('lobby.leaveTable')}
      style={{
        width: isMobile ? '100%' : ACTION_W, height: isMobile ? 48 : ACTION_H, boxSizing: 'border-box',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 12, background: '#131313', border: `0.9px solid ${DS2.color.gold}`,
        color: DS2.color.goldOutlineText,
        fontFamily: DS2.font.body, fontWeight: 800, fontSize: isMobile ? 16 : 16, letterSpacing: '0.03em', textTransform: 'uppercase',
        cursor: busy ? 'default' : 'pointer', transition: 'color .15s ease, border-color .15s ease',
      }}
    >
      {t('lobby.leaveTable')}
    </button>
  );

  if (!lobby) {
    return (
      <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ maxWidth: CONTENT_MAX, margin: '0 auto', padding: '48px 24px', flex: 1 }}>
          <div style={{ color: DT.color.textMuted, fontFamily: DS2.font.body, padding: 40 }}>{error ? renderUiMessage(t, error) : t('lobby.loading')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <style>{scopedCss}</style>
      <div style={{ position: 'relative', paddingTop: isMobile ? 20 : 32, paddingBottom: isMobile ? 28 : 40, paddingLeft: isMobile ? 16 : 'clamp(16px, 2.5vw, 32px)', paddingRight: isMobile ? 16 : 'clamp(16px, 2.5vw, 32px)', flex: '1 0 auto' }}>
        <AtmosphericGlow width={520} height={330} top={0} leftOffset={-200} radius={340} opacity={0.14} />
        <div style={{ position: 'relative', maxWidth: CONTENT_MAX, margin: '0 auto' }}>
          {/* Header */}
          <div style={eyebrowStyle(isMobile)}>{t('lobby.eyebrow')}</div>
          <h1 style={titleStyle(isMobile)}>{isHost ? t('lobby.titleHost') : t('lobby.titleGuest')}</h1>
          <p style={subtitleStyle(isMobile)}>
            {isHost ? t('lobby.subHost') : t('lobby.subGuest')}
          </p>

          {error && <div role="alert" style={{ margin: '16px 0 0', color: DT.color.error, fontFamily: DS2.font.body, fontSize: isMobile ? 13 : undefined }}>{renderUiMessage(t, error)}</div>}
          {notice && <div role="status" style={{ margin: '16px 0 0', color: DS2.color.gold, fontFamily: DS2.font.body, fontSize: isMobile ? 13 : undefined }}>{renderUiMessage(t, notice)}</div>}

          {/* Visibility (host) + primary action + Leave */}
          {controls.showVisibilityControl && (
            <div style={{ marginTop: isMobile ? 28 : 32, marginBottom: isMobile ? 12 : 14 }}><SectionLabel>{t('lobby.visibility')}</SectionLabel></div>
          )}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: controls.showVisibilityControl ? 'space-between' : 'flex-end', gap: isMobile ? 20 : 24, marginTop: controls.showVisibilityControl ? 0 : (isMobile ? 28 : 32), flexWrap: 'wrap' }}>
            {controls.showVisibilityControl && (
              <div role="radiogroup" aria-label={t('lobby.visibilityAria')} style={{ display: 'flex', gap: isMobile ? 12 : 16, flexWrap: 'wrap', flex: '1 1 460px', minWidth: isMobile ? 0 : 280 }}>
                <VisibilityCard value="public" selected={lobby.visibility === 'public'} title={t('lobby.public')} description={t('lobby.publicDesc')} onSelect={onVisibility} />
                <VisibilityCard value="private" selected={lobby.visibility === 'private'} title={t('lobby.private')} description={t('lobby.privateDesc')} onSelect={onVisibility} />
              </div>
            )}
            {/* Action group — vertically centered against the cards; Play/Get Ready
                dominant, Leave Table the equal-size secondary. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: isMobile ? 10 : 10 }}>
              {controls.showPlay && (
                <button
                  onClick={onPlay}
                  disabled={!playEnabled || busy}
                  aria-label={t('lobby.play')}
                  data-enabled={playEnabled ? 'true' : 'false'}
                  style={{
                    width: isMobile ? '100%' : ACTION_W, height: isMobile ? 48 : ACTION_H, boxSizing: 'border-box',
                    fontFamily: DS2.font.body, fontWeight: 800, fontSize: isMobile ? 18 : 18, letterSpacing: '0.03em', textTransform: 'uppercase',
                    borderRadius: 12, border: `0.85px solid ${DS2.color.gold}`,
                    background: DS2.gradient.gold, color: '#000000',
                    cursor: playEnabled && !busy ? 'pointer' : 'not-allowed',
                    // Same design, ~50% opacity while blocked (never a separate muddy block).
                    opacity: playEnabled ? 1 : 0.5,
                    boxShadow: playEnabled ? DT.shadow.gold : 'none',
                  }}
                >
                  {t('lobby.play')}
                </button>
              )}
              {controls.showGetReady && (
                <button
                  onClick={onReady}
                  disabled={busy}
                  aria-label={amReady ? t('lobby.ready') : t('lobby.getReadyAria')}
                  data-ready={amReady ? 'true' : 'false'}
                  style={{
                    width: isMobile ? '100%' : ACTION_W, height: isMobile ? 48 : ACTION_H, boxSizing: 'border-box',
                    fontFamily: DS2.font.body, fontWeight: 800, fontSize: isMobile ? 16 : 16, letterSpacing: '0.03em', textTransform: 'uppercase',
                    borderRadius: 12, border: `2px solid ${DT.color.success}`,
                    background: amReady ? DT.color.success : 'transparent',
                    color: amReady ? DT.color.white : DT.color.success,
                    cursor: busy ? 'default' : 'pointer',
                    boxShadow: amReady ? `0 0 24px -4px ${DT.color.success}` : 'none',
                  }}
                >
                  {amReady ? t('lobby.readyChecked') : t('lobby.getReady')}
                </button>
              )}
              {leaveButton}
            </div>
          </div>

          {/* Seat panel + live preview */}
          <div style={{
            marginTop: isMobile ? 28 : 32, borderRadius: DS2.radius.card, border: `1px solid ${DS2.color.gold}`,
            background: '#0B0B0B', padding: isMobile ? 16 : 'clamp(16px, 1.8vw, 26px)', boxSizing: 'border-box',
          }}>
            <div style={{ display: 'flex', gap: isMobile ? 24 : 28, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              {/* Teams */}
              <div style={{ flex: '1 1 380px', minWidth: isMobile ? 0 : 280, width: isMobile ? '100%' : undefined }}>
                <div style={{ color: DT.color.teamRed, fontFamily: DS2.font.body, fontSize: isMobile ? 15 : 15, marginBottom: 4 }}>{t('lobby.teamA')}</div>
                {[0, 2].map((pos) => {
                  const p = lobby.players.find((pl) => pl.position === pos);
                  return (
                    <SeatRow key={pos} position={pos} player={p} isMe={p?.user_id === currentUserId}
                      canManage={controls.showBotControls}
                      canRemove={isHost && !!p && !p.is_host}
                      onAddBot={onAddBot} onInvite={() => setInviteOpen(true)} onRemove={onRemove} busy={busy} />
                  );
                })}
                <div style={{ height: 1, background: DS2.color.divider, margin: '14px 0' }} />
                <div style={{ color: DT.color.teamBlue, fontFamily: DS2.font.body, fontSize: isMobile ? 15 : 15, marginBottom: 4 }}>{t('lobby.teamB')}</div>
                {[1, 3].map((pos) => {
                  const p = lobby.players.find((pl) => pl.position === pos);
                  return (
                    <SeatRow key={pos} position={pos} player={p} isMe={p?.user_id === currentUserId}
                      canManage={controls.showBotControls}
                      canRemove={isHost && !!p && !p.is_host}
                      onAddBot={onAddBot} onInvite={() => setInviteOpen(true)} onRemove={onRemove} busy={busy} />
                  );
                })}

                {controls.showBotControls && (
                  <div style={{ marginTop: isMobile ? 18 : 24 }}>
                    <SectionLabel>{t('lobby.botDifficulty')}</SectionLabel>
                    <div style={{ marginTop: 12 }}>
                      {/* Hard stays disabled/coming-soon per product rule; only labels change. */}
                      <SegmentedControl
                        ariaLabel={t('lobby.botDifficulty')}
                        value={difficulty}
                        onChange={(v) => setDifficulty(v as Difficulty)}
                        segments={[
                          { value: 'easy', label: t('lobby.difficultyEasy') },
                          { value: 'medium', label: t('lobby.difficultyMedium') },
                          { value: 'hard', label: t('lobby.difficultyHardSoon'), disabled: true },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Live, viewer-specific preview — bigger, centered in its column.
                  Empty seats are clickable and sit the viewer there, like the
                  Play Online table (move-seat, waiting only). */}
              <div style={{ flex: '1 1 280px', minWidth: isMobile ? 0 : 240, width: isMobile ? '100%' : undefined, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: isMobile ? '28px 22px' : '20px 0', boxSizing: 'border-box' }}>
                <LobbyTablePreview
                  lobby={lobby}
                  effective={effective}
                  size={isMobile ? 220 : 280}
                  onSelectSeat={(pos) => { AppSoundManager.play('click'); void run(() => apiClient.moveSeat(sessionId, pos)); }}
                />
              </div>
            </div>
          </div>

          {/* Appearance mode (Host Skin / My Skin) */}
          <div style={{ marginTop: isMobile ? 24 : 28 }}>
            <SegmentedControl
              ariaLabel={t('lobby.appearanceAria')}
              value={isHost ? 'host' : appearanceMode}
              onChange={onAppearanceMode}
              segments={[
                { value: 'host', label: t('lobby.hostSkin') },
                // The host is locked to Host Skin — My Skin is disabled for them.
                { value: 'personal', label: t('lobby.mySkin'), disabled: controls.appearanceLockedToHost },
              ]}
            />
          </div>

          {/* Table skin catalog */}
          <div style={{ marginTop: isMobile ? 24 : 28 }}>
            <SectionLabel>{t('lobby.tableSkin')}</SectionLabel>
            <div style={{ marginTop: isMobile ? 12 : 20 }}>
              <CosmeticCatalog
                ariaLabel={t('lobby.tableSkinsAria')}
                variant="table"
                items={tableItems()}
                selectedId={effective.tableSkin}
                onSelect={onSelectTable}
                readOnly={!catalogsEditable}
              />
            </div>
          </div>

          {/* Domino (tile) skin catalog */}
          <div style={{ marginTop: isMobile ? 24 : 28 }}>
            <SectionLabel>{t('lobby.tileSkin')}</SectionLabel>
            <div style={{ marginTop: isMobile ? 12 : 20 }}>
              <CosmeticCatalog
                ariaLabel={t('lobby.tileSkinsAria')}
                variant="tile"
                items={tileItems()}
                selectedId={effective.tileSkin}
                onSelect={onSelectTile}
                readOnly={!catalogsEditable}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {inviteOpen && (
        // P0 privacy: exclude already-seated friends by stable user ID (no emails).
        <InviteToGameModal
          sessionId={sessionId}
          seatedUserIds={lobby.players.map((p) => p.user_id)}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </div>
  );
}
